import { ACTIVITY_TYPE_FROM_API } from "@/features/account/config";
import { DeleteAccountSchema, UpdateEmailAddressSchema, UpdatePasswordSchema, UpdateProfileSchema, deleteAccountSchema, updateEmailAddressSchema, updatePasswordSchema, updateProfileSchema } from "@/features/account/schemas";
import { AccountActivityEntry, AccountActivityType, AccountMembership, AccountOverview } from "@/features/account/types";
import { UpdateEmailAddressRequest, UpdatePasswordRequest, UpdateProfileRequest } from "@/features/account/types/requests";
import { GetMyOverviewResponse, UpdateEmailAddressResponse, UpdateProfileResponse } from "@/features/account/types/responses";
import { ProjectRoleType } from "@/features/projects/types";
import { User } from "@/shared/lib/types/user";
import { badRequest } from "@/shared/lib/utils/errors";
import http from "@/shared/lib/utils/http";
import { toErrorDetails } from "@/shared/lib/utils/zod";

function toAccountOverview(dto: GetMyOverviewResponse): AccountOverview {
  return {
    createdAt: new Date(dto.created_at),
    lastActiveAt: new Date(dto.last_active_at),
    stats: {
      projectCount: dto.stats.project_count,
      ticketsCreated: dto.stats.tickets_created_count,
      ticketsAssigned: dto.stats.tickets_assigned_count,
      ticketsResolved: dto.stats.tickets_resolved_count,
      commentCount: dto.stats.comment_count,
    },
    memberships: dto.memberships.map(
      (membership): AccountMembership => ({
        projectId: membership.project_id,
        projectName: membership.project_name,
        roleName: (membership.role?.name ?? "Viewer") as ProjectRoleType,
        openTicketCount: membership.open_ticket_count,
        joinedAt: new Date(membership.joined_at),
      })
    ),
    recentActivity: dto.recent_activity.map(
      (activity): AccountActivityEntry => ({
        id: activity.id,
        type: (ACTIVITY_TYPE_FROM_API[activity.type] ?? activity.type) as AccountActivityType,
        projectId: activity.project_id,
        projectName: activity.project_name,
        ticketId: activity.ticket_id ?? null,
        ticketKey: activity.ticket_key ?? null,
        ticketTitle: activity.ticket_title ?? null,
        createdAt: new Date(activity.occurred_at),
      })
    ),
  };
}

export async function getMyAccountOverview(): Promise<AccountOverview> {
  const response = await http.get<GetMyOverviewResponse>({
    url: "/users/me/overview",
    contentType: "none",
  });

  return toAccountOverview(response);
}

export async function updateProfile(values: UpdateProfileSchema): Promise<Pick<User, "firstName" | "lastName">> {
  const validationResult = updateProfileSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Update Profile", toErrorDetails(validationResult.error));
  }

  const request: UpdateProfileRequest = {
    first_name: values.firstName.trim(),
    last_name: values.lastName.trim(),
  };

  const response = await http.put<UpdateProfileResponse>({
    url: "/users/me",
    body: JSON.stringify(request),
    contentType: "application/json",
  });

  return {
    firstName: response.first_name,
    lastName: response.last_name,
  };
}

export async function updateEmailAddress(values: UpdateEmailAddressSchema): Promise<string> {
  const validationResult = updateEmailAddressSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Update Email Address", toErrorDetails(validationResult.error));
  }

  const request: UpdateEmailAddressRequest = {
    email_address: values.emailAddress.trim(),
    current_password: values.currentPassword,
  };

  const response = await http.put<UpdateEmailAddressResponse>({
    url: "/users/me/email-address",
    body: JSON.stringify(request),
    contentType: "application/json",
  });

  return response.email_address;
}

export async function updatePassword(values: UpdatePasswordSchema): Promise<undefined> {
  const validationResult = updatePasswordSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Update Password", toErrorDetails(validationResult.error));
  }

  const request: UpdatePasswordRequest = {
    current_password: values.currentPassword,
    new_password: values.newPassword,
    confirm_new_password: values.confirmNewPassword,
  };

  await http.put({
    url: "/users/me/password",
    body: JSON.stringify(request),
    contentType: "application/json",
  });
}

export async function deleteAccount(values: DeleteAccountSchema): Promise<undefined> {
  const validationResult = deleteAccountSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Delete Account", toErrorDetails(validationResult.error));
  }

  await http.delete({
    url: "/users/me",
    contentType: "none",
  });
}
